import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid';
import { enqueueSnackbar } from 'notistack';
import { Warning } from '@mui/icons-material';
import { useGetPatient } from '@/api/patient';
import { PatientSchema } from '@/zod_schemas';
import { useDeletePatient } from '@/api/patient/patient';
import CustomDialog from '@/components/Dialog/CustomDialog';
import AppDataGrid, {
  GridPrimaryText,
  GridRowActions,
  GridSecondaryText,
} from '@/components/DataGrid';
import CustomNoRowsOverlay from '@/components/CustomNoRowsOverlay';

const NoPatientsOverlay = () => (
  <CustomNoRowsOverlay message="No patients yet" />
);

const PatientList = () => {
  const { data, refetch } = useGetPatient();
  const [patientData, setPatientData] = useState<PatientSchema[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const deletePatient = useDeletePatient();
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined);
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const onSubmitDialog = async () => {
    if (deleteId === undefined) {
      enqueueSnackbar('Failed to delete project', {
        variant: 'error',
      });
    } else {
      try {
        await deletePatient
          .mutateAsync(deleteId)
          .then(() => {
            enqueueSnackbar('Project delete successfully', {
              variant: 'success',
            });

            refetch();
          })
          .catch(() => {
            enqueueSnackbar('Failed to delete project', {
              variant: 'error',
            });
          });
      } catch (ex) {
        enqueueSnackbar('Error. Please contact admin', {
          variant: 'error',
        });
      }
    }
    handleDialogClose();
  };

  const navigate = useNavigate();

  useEffect(() => {
    // eslint-disable-next-line max-len
    // Normalize data to array. This API might return set of patient(array) or single patient(object)
    if (data) {
      const normalizedData = Array.isArray(data) ? data : [data];
      setPatientData(normalizedData);
    }
  }, [data]);

  const columns: GridColDef<PatientSchema>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => <GridPrimaryText>{params.value}</GridPrimaryText>,
    },
    {
      field: 'identification',
      headerName: 'Identification',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => <GridSecondaryText value={params.value} />,
    },
    {
      field: 'ethnicity',
      headerName: 'Ethnicity',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <GridSecondaryText
          value={
            Array.isArray(params.value) ? params.value.join(', ') : params.value
          }
        />
      ),
    },
    {
      field: 'date_of_birth',
      headerName: 'Date of Birth',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => <GridSecondaryText value={params.value} />,
    },
    {
      field: 'gender',
      headerName: 'Gender',
      flex: 0.7,
      minWidth: 120,
      renderCell: (params) => <GridSecondaryText value={params.value} />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      flex: 0.9,
      minWidth: 168,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <GridRowActions
          onEdit={() => navigate(`/patients/${params.row.id}`)}
          onDelete={() => {
            setDialogOpen(true);
            setDeleteId(params.row.id);
          }}
        />
      ),
    },
  ];

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          overflow: 'hidden',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 2.25,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Stack spacing={0.5}>
            <Typography variant="h3">Patient registry</Typography>
            <Typography variant="body2" color="text.secondary">
              Review patient identity, demographics, and clinical context.
            </Typography>
          </Stack>
          <Chip
            label={`${patientData.length} patient${
              patientData.length === 1 ? '' : 's'
            }`}
            size="small"
            sx={{ bgcolor: 'primary.light', color: 'primary.main' }}
          />
        </Box>
        <AppDataGrid
          rows={patientData}
          columns={columns}
          checkboxSelection
          slots={{ noRowsOverlay: NoPatientsOverlay }}
          sx={{ border: 0, borderRadius: 0 }}
        />
      </Paper>
      <CustomDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Warning color="warning" />
            <span>Delete Patient</span>
          </Stack>
        }
        onSubmit={onSubmitDialog}
      >
        <div>Are you sure you want to delete patient?</div>
      </CustomDialog>
    </>
  );
};

export default PatientList;
